import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
    CALCULATOR_TYPE,
    DEFAULT_ACCOUNT_INDEX,
    ORDER_STATUS,
    POSITION_MARGIN_TYPES,
    POSITION_SIDE,
    PRICE_TYPE,
    SOCKET_TOPICS,
    TRADE_TYPE,
} from 'src/common/constants';
import { errorHandler, roundNumber } from 'src/common/helpers';
import { API_ENDPOINTS } from 'src/setup/client/api-endpoints';
import HttpClient from 'src/setup/client/http-client';
import {
    ActiveOrder,
    CalculatorQueryParamsType,
    CancelOrderPayload,
    ConditionalOrder,
    DerivativeAsset,
    FetchAvailableAssets,
    FundingRate,
    FutureOrderPayload,
    FuturePositionsPayload,
    FutureSymbol,
    FutureSymbolPositionsPayload,
    FutureSymbolSettings,
    FutureSymbolSettingsPayload,
    FutureTradeAble,
    GetLeveragePayload,
    Leverage,
    ModifyPositionMarginPayLoad,
    MyTrade,
    Position,
    PositionStopProfitLoss,
    RiskLimitPayload,
    SetLeveragePayload,
    SetStopProfitLossPayLoad,
    StopProfitLossPayLoad,
    Toast,
    TradeOrder,
} from 'src/types';

import { RootState } from '../Store';

interface DerivativesState {
    loading: Record<string, boolean>;
    error: Record<string, any>;
    fundingRate: FundingRate;
    currentSymbol: FutureSymbol;
    assets: DerivativeAsset;
    symbolSettings: FutureSymbolSettings;
    currentSymbolPositions: Array<Position>;
    positions: Array<Position>;
    activeOrders: Array<ActiveOrder>;
    conditionalOrders: Array<ConditionalOrder>;
    tradeOrders: Array<TradeOrder>;
    myTrades: Array<MyTrade>;
    orderCreated: number;
    tradeable: FutureTradeAble[];
    transferAbleAmount: string | number;
    createOrderPrice: string;
    tvWidgetRef: any;
    toast: Toast;
    positionTrigger: string;
    longPositionStopProfitLoss: PositionStopProfitLoss;
    shortPositionStopProfitLoss: PositionStopProfitLoss;
    leverage: Leverage;
}

const initialState: DerivativesState = {
    loading: {},
    error: {},
    currentSymbol: {} as FutureSymbol,
    assets: {} as DerivativeAsset,
    symbolSettings: {
        symbolId: '',
        orderFee: {
            symbolId: '',
            symbolName: '',
            takerFeeRate: '',
            makerFeeRate: '',
        },
        orderSetting: {
            isPassiveOrder: false,
            timeInForce: '',
            isConfirm: true,
        },
        riskLimit: [
            {
                riskLimitId: '',
                riskLimitAmount: '',
                maintainMargin: '',
                initialMargin: '',
                side: '',
            },
            {
                riskLimitId: '',
                riskLimitAmount: '',
                maintainMargin: '',
                initialMargin: '',
                side: '',
            },
        ],
    },
    currentSymbolPositions: [] as Array<Position>, // TODO:: remove currentSymbolPositions. if needed get current symbol position from positions state instead of API call
    positions: [] as Array<Position>,
    orderCreated: 0,
    activeOrders: [] as Array<ActiveOrder>,
    conditionalOrders: [] as Array<ConditionalOrder>,
    tradeOrders: [] as Array<TradeOrder>,
    myTrades: [] as Array<MyTrade>,
    tradeable: [] as FutureTradeAble[],
    transferAbleAmount: 0,
    createOrderPrice: '',
    tvWidgetRef: null,
    toast: {} as Toast,
    positionTrigger: '',
    longPositionStopProfitLoss: {} as PositionStopProfitLoss,
    shortPositionStopProfitLoss: {} as PositionStopProfitLoss,
    leverage: { leverageLong: 0, leverageShort: 0 } as Leverage,
    fundingRate: undefined,
};

// Async thunk actions and status keys
export const AsyncActionKeys = {
    fetchFundingRate: 'fetchFundingRate',
    fetchAvailableAssets: 'fetchAvailableAssets',
    fetchSymbolSettings: 'fetchSymbolSettings',
    fetchSymbolPositions: 'fetchSymbolPositions',
    createOrder: 'createOrder',
    performCalculations: 'performCalculations',
    setSymbolSettings: 'setSymbolSettings',
    fetchPositions: 'fetchPositions',
    fetchActiveOrders: 'fetchActiveOrders',
    fetchConditionalOrders: 'fetchConditionalOrders',
    fetchTradeOrders: 'fetchTradeOrders',
    fetchMyTrades: 'fetchMyTrades',
    cancelActiveOrder: 'cancelActiveOrder',
    cancelConditionalOrder: 'cancelConditionalOrder',
    setRiskLimit: 'setRiskLimit',
    fetchPositionStopProfitLoss: 'fetchPositionStopProfitLoss',
    setPositionStopProfitLoss: 'setPositionStopProfitLoss',
    cancelPositionStopProfitLoss: 'cancelPositionStopProfitLoss',
    modifyPositionMargin: 'modifyPositionMargin',
    getLeverage: 'getLeverage',
    setLeverage: 'setLeverage',
};

export const fetchFundingRate = createAsyncThunk(
    AsyncActionKeys.fetchFundingRate,
    async (queryParams: String, thunkAPI) => {
        try {
            const response = await HttpClient.get(`${API_ENDPOINTS.FUNDING_RATES}?${queryParams}`);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchAvailableAssets = createAsyncThunk(
    AsyncActionKeys.fetchAvailableAssets,
    async (body: FetchAvailableAssets, thunkAPI) => {
        try {
            const response = await HttpClient.post(API_ENDPOINTS.AVAILABLE_FUTURE_ASSETS, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchSymbolSettings = createAsyncThunk(
    AsyncActionKeys.fetchSymbolSettings,
    async (body: FutureSymbolSettingsPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(API_ENDPOINTS.GET_FUTURE_ORDER_SETTING, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchSymbolPositions = createAsyncThunk(
    AsyncActionKeys.fetchSymbolPositions,
    async (body: FutureSymbolPositionsPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(API_ENDPOINTS.FUTURE_POSITIONS, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const createOrder = createAsyncThunk(
    AsyncActionKeys.createOrder,
    async (body: FutureOrderPayload, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;
        const userCurrentFutureTradeAccountIndex = state.global.userCurrentFutureTradeAccountIndex;
        try {
            const response = await HttpClient.post(`${API_ENDPOINTS.CREATE_FUTURE_ORDER}`, body);
            return response.data;
        } catch (error) {
            error.userCurrentFutureTradeAccountIndex = userCurrentFutureTradeAccountIndex;
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const performCalculations = createAsyncThunk(
    AsyncActionKeys.performCalculations,
    async (queryParams: CalculatorQueryParamsType, thunkAPI) => {
        try {
            const response = await HttpClient.get(
                `${[
                    queryParams.calculatorType === CALCULATOR_TYPE.ROR
                        ? API_ENDPOINTS.CALCULATE_PROFIT_INFO
                        : API_ENDPOINTS.CALCULATE_LIQUIDATION_PRICE,
                ]}`,
                { params: queryParams },
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const setSymbolSettings = createAsyncThunk(
    AsyncActionKeys.setSymbolSettings,
    async (body: FutureSymbolSettingsPayload, thunkAPI) => {
        try {
            await HttpClient.post(`${API_ENDPOINTS.SET_FUTURE_ORDER_SETTING}`, body);
            const response = await HttpClient.post(API_ENDPOINTS.GET_FUTURE_ORDER_SETTING, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchPositions = createAsyncThunk(
    AsyncActionKeys.fetchPositions,
    async (body: FuturePositionsPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(API_ENDPOINTS.ORDER_POSITIONS, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchActiveOrders = createAsyncThunk(
    AsyncActionKeys.fetchActiveOrders,
    async (queryParams: string, thunkAPI) => {
        try {
            const response = await HttpClient.get(
                `${API_ENDPOINTS.FUTURE_OPEN_ORDERS}?${queryParams}`,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchConditionalOrders = createAsyncThunk(
    AsyncActionKeys.fetchConditionalOrders,
    async (queryParams: string, thunkAPI) => {
        try {
            const response = await HttpClient.get(
                `${API_ENDPOINTS.FUTURE_OPEN_ORDERS}?${queryParams}`,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchTradeOrders = createAsyncThunk(
    AsyncActionKeys.fetchTradeOrders,
    async (queryParams: string, thunkAPI) => {
        try {
            const response = await HttpClient.get(
                `${API_ENDPOINTS.FUTURE_TRADE_ORDERS}?${queryParams}`,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchMyTrades = createAsyncThunk(
    AsyncActionKeys.fetchMyTrades,
    async (queryParams: string, thunkAPI) => {
        try {
            const response = await HttpClient.get(
                `${API_ENDPOINTS.FUTURE_MY_TRADES}?${queryParams}`,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const cancelActiveOrder = createAsyncThunk(
    AsyncActionKeys.cancelActiveOrder,
    async (body: CancelOrderPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(`${API_ENDPOINTS.CANCEL_ORDER}`, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const cancelConditionalOrder = createAsyncThunk(
    AsyncActionKeys.cancelConditionalOrder,
    async (body: CancelOrderPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(`${API_ENDPOINTS.CANCEL_ORDER}`, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const setRiskLimit = createAsyncThunk(
    AsyncActionKeys.setRiskLimit,
    async (body: RiskLimitPayload, thunkAPI) => {
        try {
            await HttpClient.post(`${API_ENDPOINTS.SET_RISK_LIMIT}`, body);
            const response = await HttpClient.post(API_ENDPOINTS.GET_FUTURE_ORDER_SETTING, {
                exchange_id: body.exchange_id,
                symbol_ids: body.symbol_id,
                account_index: body.account_index,
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchPositionStopProfitLoss = createAsyncThunk(
    AsyncActionKeys.fetchPositionStopProfitLoss,
    async (body: StopProfitLossPayLoad, thunkAPI) => {
        try {
            const response = await HttpClient.post(
                `${API_ENDPOINTS.GET_POSITION_STOP_PROFIT_LOSS}`,
                body,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const setPositionStopProfitLoss = createAsyncThunk(
    AsyncActionKeys.setPositionStopProfitLoss,
    async (body: SetStopProfitLossPayLoad, thunkAPI) => {
        try {
            const response = await HttpClient.post(
                `${API_ENDPOINTS.SET_POSITION_STOP_PROFIT_LOSS}`,
                body,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const cancelPositionStopProfitLoss = createAsyncThunk(
    AsyncActionKeys.cancelPositionStopProfitLoss,
    async (body: SetStopProfitLossPayLoad, thunkAPI) => {
        try {
            const response = await HttpClient.post(
                `${API_ENDPOINTS.CANCEL_POSITION_STOP_PROFIT_LOSS}`,
                body,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const modifyPositionMargin = createAsyncThunk(
    AsyncActionKeys.modifyPositionMargin,
    async (body: ModifyPositionMarginPayLoad, thunkAPI) => {
        try {
            const response = await HttpClient.post(`${API_ENDPOINTS.MODIFY_POSITION_MARGIN}`, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const getLeverage = createAsyncThunk(
    AsyncActionKeys.getLeverage,
    async (body: GetLeveragePayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(`${API_ENDPOINTS.GET_LEVERAGE}`, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const setLeverage = createAsyncThunk(
    AsyncActionKeys.setLeverage,
    async (body: SetLeveragePayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(`${API_ENDPOINTS.SET_LEVERAGE}`, body);
            if (response?.data?.success) {
                await thunkAPI.dispatch(
                    getLeverage({
                        symbol_id: body.symbol_id,
                        account_index: body.account_index,
                    }),
                );
            }
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

//helper to filter orders
function filterOrders(order: ActiveOrder | TradeOrder, orders: TradeOrder[] | ActiveOrder[]) {
    const orderExist = orders.some((currentOrder) => currentOrder.orderId === order.orderId);
    if (orderExist) {
        return orders.map((currentOrder) => {
            if (currentOrder.orderId === order.orderId) {
                return order;
            }
            return currentOrder;
        });
    } else {
        return [order as TradeOrder, ...orders];
    }
}

//slice
export const derivativesSlice = createSlice({
    name: 'derivativesSlice',
    initialState,
    reducers: {
        showNotification: (state, action) => {
            state.toast = {
                id: new Date().getTime().toString(),
                title: action.payload.title,
                type: action.payload.type,
                description: action.payload.description && action.payload.description,
            };
        },
        updateFundingRate: (state, action: PayloadAction<FundingRate>) => {
            state.fundingRate = action.payload;
        },
        saveCurrentSymbol: (state, action: PayloadAction<FutureSymbol>) => {
            state.currentSymbol = action.payload;
            localStorage.setItem('currentFutureSymbol', action.payload.symbolId);
        },
        updateUserTradeData: (state, action) => {
            const { data, topic } = action.payload;
            if (data) {
                switch (topic) {
                    case SOCKET_TOPICS.FUTURES_ORDER:
                        data.forEach((order: TradeOrder | ActiveOrder) => {
                            if (
                                order.status === ORDER_STATUS.NEW ||
                                order.status === ORDER_STATUS.PARTIALLY_FILLED
                            ) {
                                const orders = filterOrders(order, state.activeOrders);
                                state.activeOrders = orders as ActiveOrder[];
                                state.conditionalOrders = state.conditionalOrders.filter(
                                    (currentOrder) =>
                                        currentOrder.clientOrderId !== order.clientOrderId,
                                );
                            } else if (
                                order.status === ORDER_STATUS.FILLED ||
                                order.status === ORDER_STATUS.CANCELED
                            ) {
                                state.activeOrders = state.activeOrders.filter(
                                    (currentOrder) => currentOrder.orderId !== order.orderId,
                                );
                            }
                            state.tradeOrders = filterOrders(order, state.tradeOrders);
                            if (
                                order.status === ORDER_STATUS.FILLED ||
                                order.status === ORDER_STATUS.PARTIALLY_FILLED
                            ) {
                                const values = {
                                    quantity: order.executedQty,
                                    name: order.symbolId.replace(/-.*/, ''),
                                    price:
                                        order.priceType === PRICE_TYPE.MARKET
                                            ? 'market'
                                            : order.avgPrice,
                                };
                                state.toast = {
                                    id: new Date().getTime().toString(),
                                    title:
                                        order.status === ORDER_STATUS.FILLED
                                            ? 'order_filled'
                                            : 'order_partially_filled',
                                    description:
                                        order.side === TRADE_TYPE.BUY_OPEN ||
                                        order.side === TRADE_TYPE.BUY_CLOSE
                                            ? 'order_filled_open_long_description'
                                            : order.side === TRADE_TYPE.SELL_OPEN ||
                                              order.side === TRADE_TYPE.SELL_CLOSE
                                            ? 'order_filled_open_short_description'
                                            : '',
                                    type: 'info',
                                    values,
                                };
                            }
                        });
                        state.activeOrders.sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
                        state.tradeOrders.sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
                        break;
                    case SOCKET_TOPICS.FUTURES_POSITION:
                        {
                            if (data.length < state.positions.length) {
                                const removePositions = state.positions.filter(
                                    (position) =>
                                        !data.some(
                                            (pos: Position) =>
                                                pos.positionId === position.positionId,
                                        ),
                                );
                                removePositions.forEach((removePosition: Position) => {
                                    if (removePosition.isLong === POSITION_SIDE.LONG) {
                                        delete state.longPositionStopProfitLoss[
                                            removePosition.symbolId
                                        ];
                                    } else {
                                        delete state.shortPositionStopProfitLoss[
                                            removePosition.symbolId
                                        ];
                                    }
                                });
                            }

                            state.positions = data;
                        }
                        break;
                    case SOCKET_TOPICS.FUTURES_TRADEABLE:
                        state.tradeable = data;
                        break;
                    default:
                        break;
                }
            }
        },
        updateTvWidgetRef: (state, action) => {
            state.tvWidgetRef = action.payload;
        },
        updateCreateOrderPrice: (state, action) => {
            state.createOrderPrice = action.payload;
        },
        removeLoggedInUserData: (state) => {
            state.positions = [] as Array<Position>;
            state.activeOrders = [] as Array<ActiveOrder>;
            state.conditionalOrders = [] as Array<ConditionalOrder>;
            state.tradeOrders = [] as Array<TradeOrder>;
            state.myTrades = [] as Array<MyTrade>;
            state.positionTrigger = '';
            state.longPositionStopProfitLoss = {} as PositionStopProfitLoss;
            state.shortPositionStopProfitLoss = {} as PositionStopProfitLoss;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchFundingRate.fulfilled, (state, action) => {
                state.fundingRate = action.payload[0];
            })
            .addCase(fetchAvailableAssets.fulfilled, (state, action) => {
                state.assets =
                    action.payload.find(
                        (asset: DerivativeAsset) =>
                            asset.tokenId === state.currentSymbol.quoteTokenId,
                    ) || ({} as DerivativeAsset);
            })
            .addCase(fetchSymbolSettings.fulfilled, (state, action) => {
                state.symbolSettings = action.payload[0];
            })
            .addCase(fetchSymbolPositions.fulfilled, (state, action) => {
                state.currentSymbolPositions = action.payload;
            })
            .addCase(createOrder.fulfilled, (state, action) => {
                const order = action.payload;
                state.orderCreated += 1;
                if (
                    order.status === ORDER_STATUS.NEW ||
                    order.status === ORDER_STATUS.PARTIALLY_FILLED
                ) {
                    const orders = filterOrders(order, state.activeOrders);
                    state.activeOrders = orders as ActiveOrder[];
                    state.tradeOrders = filterOrders(order, state.tradeOrders);
                } else if (
                    order.status === ORDER_STATUS.FILLED ||
                    order.status === ORDER_STATUS.CANCELED
                ) {
                    state.activeOrders = state.activeOrders.filter(
                        (currentOrder) => currentOrder.orderId !== order.orderId,
                    );
                    state.tradeOrders = filterOrders(order, state.tradeOrders);
                } else if (order.status === ORDER_STATUS.ORDER_NEW) {
                    const orders = filterOrders(order, state.conditionalOrders);
                    state.conditionalOrders = orders as ConditionalOrder[];
                }
                const values = {
                    quantity: order.origQty,
                    name: order.symbolId.replace(/-.*/, ''),
                    price: order.priceType === PRICE_TYPE.MARKET ? 'market' : order.price,
                };

                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'order_submitted',
                    description:
                        order.side === TRADE_TYPE.BUY_OPEN || order.side === TRADE_TYPE.BUY_CLOSE
                            ? 'order_submitted_open_long_description'
                            : order.side === TRADE_TYPE.SELL_OPEN ||
                              order.side === TRADE_TYPE.SELL_CLOSE
                            ? 'order_submitted_open_short_description'
                            : '',
                    type: 'success',
                    values,
                };
            })
            .addCase(createOrder.rejected, (state, action: any) => {
                const userCurrentFutureTradeAccountIndex =
                    action.payload?.userCurrentFutureTradeAccountIndex;
                const errorCode = action.payload?.response?.data?.code;
                if (
                    userCurrentFutureTradeAccountIndex !== DEFAULT_ACCOUNT_INDEX &&
                    errorCode === 33001
                ) {
                    state.toast = {
                        id: new Date().getTime().toString(),
                        title: 'futures_bonus_insufficient_balance',
                        type: 'error',
                    };
                } else {
                    const error = errorHandler(action.payload);
                    state.toast = error;
                }
            })
            .addCase(setSymbolSettings.fulfilled, (state, action) => {
                state.symbolSettings = action.payload[0];
            })
            .addCase(setSymbolSettings.rejected, (state, action) => {
                const error = errorHandler(action.payload);
                state.toast = error;
            })
            .addCase(fetchPositions.fulfilled, (state, action) => {
                state.positions = action.payload;
                state.positionTrigger = 'positions_fetched';
            })
            .addCase(fetchActiveOrders.fulfilled, (state, action) => {
                state.activeOrders = action.payload;
            })
            .addCase(fetchConditionalOrders.fulfilled, (state, action) => {
                state.conditionalOrders = action.payload;
            })
            .addCase(fetchTradeOrders.fulfilled, (state, action) => {
                state.tradeOrders = action.payload;
            })
            .addCase(fetchMyTrades.fulfilled, (state, action) => {
                state.myTrades = action.payload;
            })
            .addCase(cancelActiveOrder.fulfilled, (state, action) => {
                const canceledOrder: ActiveOrder = action.payload;
                state.activeOrders = state.activeOrders.filter(
                    (order: ActiveOrder) => order.orderId !== canceledOrder.orderId,
                );
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'order_cancelled',
                    type: 'success',
                };
            })
            .addCase(cancelActiveOrder.rejected, (state, action) => {
                const error = errorHandler(action.payload);
                state.toast = error;
            })
            .addCase(cancelConditionalOrder.fulfilled, (state, action) => {
                const canceledOrder: ConditionalOrder = action.payload;
                state.conditionalOrders = state.conditionalOrders.filter(
                    (order: ConditionalOrder) => order.orderId !== canceledOrder.orderId,
                );
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'order_cancelled',
                    type: 'success',
                };
            })
            .addCase(cancelConditionalOrder.rejected, (state, action) => {
                const error = errorHandler(action.payload);
                state.toast = error;
            })
            .addCase(setRiskLimit.fulfilled, (state, action) => {
                state.symbolSettings = action.payload[0];
            })
            .addCase(setRiskLimit.rejected, (state, action: any) => {
                const errorCode = action.payload?.response?.data?.code;
                switch (errorCode) {
                    case 31023:
                        state.toast = {
                            id: new Date().getTime().toString(),
                            title: 'error_invalid_leverage_title',
                            description: 'error_invalid_leverage_description',
                            type: 'error',
                        };
                        break;
                    default:
                        state.toast = errorHandler(action.payload);
                }
            })
            .addCase(fetchPositionStopProfitLoss.fulfilled, (state, action) => {
                const { is_long, symbol_id } = action.meta.arg;
                const response = action.payload;
                const data = { ...response, symbolId: symbol_id, isLong: is_long, isSet: false };
                if (data.isLong.toString() == POSITION_SIDE.LONG) {
                    state.longPositionStopProfitLoss[data.symbolId] = data;
                } else {
                    state.shortPositionStopProfitLoss[data.symbolId] = data;
                }
                state.positionTrigger = 'position_stop_loss_added';
            })
            .addCase(setPositionStopProfitLoss.fulfilled, (state, action) => {
                const { is_long, symbol_id } = action.meta.arg;
                const response = action.payload;
                const data = { ...response, symbolId: symbol_id, isLong: is_long, isSet: true };
                if (data.isLong.toString() == POSITION_SIDE.LONG) {
                    state.longPositionStopProfitLoss[data.symbolId] = data;
                } else {
                    state.shortPositionStopProfitLoss[data.symbolId] = data;
                }
                state.positionTrigger = 'position_stop_loss_added';
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'tp_sl_update_success',
                    type: 'success',
                };
            })
            .addCase(setPositionStopProfitLoss.rejected, (state, action) => {
                const error = errorHandler(action.payload);
                state.toast = error;
            })
            .addCase(cancelPositionStopProfitLoss.fulfilled, (state, action) => {
                const { symbol_id: symbolId, is_long: isLong } = action.meta.arg;
                const payload: any = { isLong, symbolId };

                if (isLong.toString() === POSITION_SIDE.LONG) {
                    state.longPositionStopProfitLoss[payload.symbolId] = payload;
                } else {
                    state.shortPositionStopProfitLoss[payload.symbolId] = payload;
                }
                state.positionTrigger = 'position_stop_loss_cancel';
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'tp_sl_update_cancelled',
                    type: 'success',
                };
            })
            .addCase(cancelPositionStopProfitLoss.rejected, (state, action) => {
                const error = errorHandler(action.payload);
                state.toast = error;
            })
            .addCase(modifyPositionMargin.fulfilled, (state, action) => {
                const { type, amount } = action.meta.arg;

                state.assets.availableMargin = (
                    type === POSITION_MARGIN_TYPES.INCREASE
                        ? Number(state.assets.availableMargin) - Number(amount)
                        : Number(state.assets.availableMargin) + Number(amount)
                ).toString();

                state.toast = {
                    id: new Date().getTime().toString(),
                    title:
                        type === POSITION_MARGIN_TYPES.INCREASE
                            ? 'added_margin_successful'
                            : 'removed_margin_successful',
                    type: 'success',
                };
            })
            .addCase(modifyPositionMargin.rejected, (state, action) => {
                const error = errorHandler(action.payload);
                state.toast = error;
            })
            .addCase(getLeverage.fulfilled, (state, action) => {
                state.leverage = {
                    leverageLong: roundNumber(Number(action.payload.leverageLong), 1),
                    leverageShort: roundNumber(Number(action.payload.leverageShort), 1),
                };
            })
            .addCase(setLeverage.fulfilled, (state) => {
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'leverage_updated',
                    type: 'success',
                };
            })
            .addCase(setLeverage.rejected, (state, action: any) => {
                const errorCode = action.payload?.response?.data?.code;
                let title = '';
                let description = '';
                switch (errorCode) {
                    case 32144:
                        title = 'error_leverage_high_title';
                        description = 'error_leverage_high_description';
                        break;
                    case 32145:
                        title = 'error_invalid_margin_title';
                        description = 'error_invalid_margin_description';
                        break;
                    case 32146:
                        title = 'error_leverage_adjustment_title';
                        description = 'error_leverage_adjustment_description';
                        break;
                    case 33001:
                        title = 'error_insufficient_fund_title';
                        description = 'error_insufficient_fund_description';
                        break;
                    default:
                        title = errorHandler(action.payload?.error).title;
                        break;
                }
                state.toast = {
                    id: new Date().getTime().toString(),
                    title,
                    description,
                    type: 'error',
                };
            });

        // Loading and error handling for async actions
        builder.addMatcher(
            (action) => action.type.endsWith('/pending'),
            (state, action) => {
                const actionType = action.type.split('/')[0];

                state.loading[actionType] = true;
            },
        );
        builder.addMatcher(
            (action) => action.type.endsWith('/fulfilled'),
            (state, action) => {
                const actionType = action.type.split('/')[0];

                state.loading[actionType] = false;
                state.error[actionType] = undefined;
            },
        );
        builder.addMatcher(
            (action) => action.type.endsWith('/rejected'),
            (state, action) => {
                const actionType = action.type.split('/')[0];

                state.loading[actionType] = false;
                state.error[actionType] = action.payload;
            },
        );
    },
});

export const {
    showNotification,
    updateFundingRate,
    updateCreateOrderPrice,
    updateTvWidgetRef,
    updateUserTradeData,
    removeLoggedInUserData,
    saveCurrentSymbol,
} = derivativesSlice.actions;
